import React, { useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import { BusinessOutlined, PostAddOutlined, SupervisorAccount } from '@mui/icons-material';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import { useSelector } from 'react-redux';
import get from 'lodash/get';

import { useGetOrganization, useGetUsers } from 'api';
import { selectIsAdministrator, selectPreferences } from 'features/logon/logonSlice';

import DashboardLayout from 'components/dashboardLayout/dashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import ToastMessage from 'components/toastMessage';
import { Button, Loader } from 'components/ui';
import { SettingsBanking } from './SettingsBanking/SettingsBanking';
import { SettingsDocumentAutosending } from './SettingsDocumentAutosending/SettingsDocumentAutosending';
import { SettingsUnpaidInvoicesMonitoring } from './SettingsUnpaidInvoicesMonitoring/SettingsUnpaidInvoicesMonitoring';
import { SettingsUsers } from './SettingsUsers/SettingsUsers';
import { SettingsOrganization } from './SettingsOrganization/SettingsOrganization';
import { resources } from 'oems/resources';

import * as Styled from './Settings.styles';
import { SettingsPreferences } from './SettingsPreferences/SettingsPreferences';
import { useGetAuthenticatedUser } from 'api/endpoints/getAuthenticatedUser';

export const Settings = () => {
  const [toastData, setToastData] = useState(null);
  const isAdministrator = useSelector(selectIsAdministrator);  

  const {
    data: organizationData,
    refetch: refetchOrganizationData,
    isLoading: isLoadingOrganizationData,
  } = useGetOrganization();

  const { data: usersData, refetch: refetchUsersData, isLoading: isLoadingUsersData } = useGetUsers();
  const { data: userData, refetch: refetchUserData } = useGetAuthenticatedUser();

  const userPreferences = useMemo(() => get(userData, 'user.preferences', {}), [userData]);

  return (
    <DashboardLayout>
      {toastData && (
        <ToastMessage
          open
          onClose={() => setToastData(null)}
          severity={toastData.severity}
          message={toastData.message}
        />
      )}

      <DashboardNavbar />

      {isLoadingOrganizationData || isLoadingUsersData ? (
        <Loader />
      ) : (
        <Styled.Wrapper>
          <Grid container spacing={3}>
            <Grid item xs={12} lg={4} xxl={3}>
              <Styled.Menu>
                <Button
                  variant="text"
                  color="secondary"
                  className="menuItem"
                  onClick={() =>
                    document.getElementById('settings-organization').scrollIntoView({ behavior: 'smooth' })
                  }
                >
                  <BusinessOutlined /> Dane firmy
                </Button>

                {isAdministrator && (
                  <Button
                    variant="text"
                    color="secondary"
                    className="menuItem"
                    onClick={() => document.getElementById('settings-users').scrollIntoView({ behavior: 'smooth' })}
                  >
                    <SupervisorAccount /> Użytkownicy
                  </Button>
                )}

                <Button
                  variant="text"
                  color="secondary"
                  className="menuItem"
                  onClick={() => document.getElementById('settings-banking').scrollIntoView({ behavior: 'smooth' })}
                >
                  <resources.BankAccountsIcon /> Rachunki bankowe
                </Button>

                <Button
                  variant="text"
                  color="secondary"
                  className="menuItem"
                  onClick={() => document.getElementById('settings-documents').scrollIntoView({ behavior: 'smooth' })}
                >
                  <resources.EmailSendIcon /> Wysyłka dokumentów
                </Button>

                <Button
                  variant="text"
                  color="secondary"
                  className="menuItem"
                  onClick={() => document.getElementById('settings-user-preferences').scrollIntoView({ behavior: 'smooth' })}
                >
                  <ManageAccountsOutlinedIcon /> Preferencje użytkownika
                </Button>

                <Button
                  variant="text"
                  color="secondary"
                  className="menuItem"
                  onClick={() => document.getElementById('settings-unpaid-invoices-monitoring').scrollIntoView({ behavior: 'smooth' })}
                >
                  <resources.QueryStatsIcon /> Monitoring nieopł. faktur
                </Button>
              </Styled.Menu>
            </Grid>

            <Grid item xs={12} lg={8} xxl={9}>
              <Styled.Content>
                <div id="settings-organization">
                  <SettingsOrganization
                    organizationData={organizationData}
                    refetchOrganizationData={refetchOrganizationData}
                  />
                </div>

                {isAdministrator && (
                  <div id="settings-users">
                    <SettingsUsers usersData={usersData} refetchUsersData={refetchUsersData} />
                  </div>
                )}

                <div id="settings-banking">
                  <SettingsBanking
                    organizationData={organizationData}
                    refetchOrganizationData={refetchOrganizationData}
                  />
                </div>

                <div id="settings-documents">
                  <SettingsDocumentAutosending
                    organizationData={organizationData}
                    refetchOrganizationData={refetchOrganizationData}
                  />
                </div>

                <div id="settings-user-preferences">
                  <SettingsPreferences
                    userPreferences={userPreferences}
                    refetchUserData={refetchUserData}
                  />
                </div>

                <div id="settings-unpaid-invoices-monitoring">
                  <SettingsUnpaidInvoicesMonitoring
                    organizationData={organizationData}
                    refetchOrganizationData={refetchOrganizationData}
                  />
                </div>
              </Styled.Content>
            </Grid>
          </Grid>
        </Styled.Wrapper>
      )}
    </DashboardLayout>
  );
};
